import {
	useCallback,
	useMemo
} from "react";
import { useLocalStorageState, useUserScopedKey } from "client/Hooks";
import { Emoji, EmojisWithGroup } from "./useEmojiPickerData";
import { isNotNil } from "client/Misc/utils";

type UseRecentEmojisResult = {
	recentEmojis: readonly Emoji[];
	registerRecentEmoji: (emojiUnicode: string) => void;
};

export const useRecentEmojis = (
	emojiData: readonly EmojisWithGroup[]
): UseRecentEmojisResult => {
	const storageKey: string = useUserScopedKey(RECENT_EMOJI_STORAGE_KEY);
	const [recentEmojiUnicodes, setRecentEmojiUnicodes] = useLocalStorageState<readonly string[]>(
		storageKey,
		[]
	);

	const emojisByUnicode = useMemo(
		(): ReadonlyMap<string, Emoji> => {
			const result: Map<string, Emoji> = new Map();

			for (const group of emojiData) {
				for (const emoji of group.emojis) {
					result.set(emoji.unicode, emoji);
				}
			}

			return result;
		},
		[emojiData]
	);

	const recentEmojis: readonly Emoji[] = useMemo(
		(): readonly Emoji[] => {
			return recentEmojiUnicodes.map(
				(emojiUnicode: string): Emoji | undefined => {
					return emojisByUnicode.get(emojiUnicode);
				}
			)
				.filter(isNotNil);
	},
		[emojisByUnicode, recentEmojiUnicodes]
	);

	const registerRecentEmoji = useCallback(
		(emojiUnicode: string): void => {
			const emojiWithoutSkinTone: string = removeSkinTone(emojiUnicode);

			setRecentEmojiUnicodes(
				(persistedRecentEmojiUnicodes: readonly string[]): readonly string[] => {
					return [
						emojiWithoutSkinTone,
						...persistedRecentEmojiUnicodes.filter(
							(recentEmojiUnicode: string): boolean => recentEmojiUnicode !== emojiWithoutSkinTone
						)
					].slice(0, MAX_RECENT_EMOJI_COUNT);
				}
			);
		},
		[setRecentEmojiUnicodes]
	);

	return {
		recentEmojis,
		registerRecentEmoji
	};
};

const RECENT_EMOJI_STORAGE_KEY = "emoji-picker-recent-emojis";
const MAX_RECENT_EMOJI_COUNT = 20;

export const removeSkinTone = (unicode: string): string => {
	return unicode.replace(/[\u{1F3FB}-\u{1F3FF}]/gu, "");
};
